import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";

export default function Nav({ content }) {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [visible, setVisible] = useState(true);
    const [lastY, setLastY] = useState(0);

    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY;
            setScrolled(y > 40);
            if (y < 80) {
                setVisible(true);
            } else if (y > lastY + 4) {
                setVisible(false);
            } else if (y < lastY - 4) {
                setVisible(true);
            }
            setLastY(y);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [lastY]);

    const links = [
        { label: "Services", href: "#services" },
        { label: "Gallery", href: "#gallery" },
        { label: "About", href: "#about" },
        { label: "Reviews", href: "#reviews" },
        { label: "Contact", href: "#contact" },
    ];

    return (
        <motion.nav
            data-testid="site-nav"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: visible ? 0 : -100, opacity: visible ? 1 : 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className={`fixed top-0 left-0 right-0 z-50 ${
                scrolled ? "bg-white/85 backdrop-blur-xl border-b border-neutral-200/70" : "bg-transparent"
            } transition-colors duration-300`}
        >
            <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-4 flex items-center justify-between">
                <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
                    <img src={content.logo_url} alt="Steven R. Construction" className="h-10 w-auto" />
                    <div className="hidden sm:flex flex-col leading-none">
                        <span className="font-display font-bold text-[15px] tracking-tight text-neutral-900">HOME RENOVATION</span>
                        <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mt-1">Steven R. Construction LLC</span>
                    </div>
                </a>

                <div className="hidden md:flex items-center gap-8">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className="text-sm font-medium text-neutral-800 hover:text-[color:var(--brand-blue)] transition-colors relative group"
                        >
                            {l.label}
                            <span className="absolute left-0 -bottom-1 h-px w-0 bg-[color:var(--brand-blue)] group-hover:w-full transition-all duration-300" />
                        </a>
                    ))}
                    <a
                        href={`tel:${content.phone_raw}`}
                        data-testid="nav-call-btn"
                        className="btn-brand inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] px-5 py-2.5 text-sm font-semibold text-white"
                    >
                        <Phone className="h-4 w-4" />
                        {content.phone}
                    </a>
                </div>

                <button
                    className="md:hidden text-neutral-900"
                    onClick={() => setOpen((o) => !o)}
                    data-testid="nav-mobile-toggle"
                    aria-label="Menu"
                >
                    {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="md:hidden overflow-hidden bg-white border-t border-neutral-200"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {links.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    className="text-base font-medium text-neutral-900"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href={`tel:${content.phone_raw}`}
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-blue)] px-5 py-3 text-sm font-semibold text-white"
                            >
                                <Phone className="h-4 w-4" /> {content.phone}
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.nav>
    );
}
